import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ISimCard, IModem } from '@/types/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

interface OwnerSummaryData {
  owner: string;
  sims: ISimCard[];
  modems: IModem[];
}

const OwnerSummary: React.FC = () => {
  const { owner } = useParams<{ owner: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<OwnerSummaryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedMessage, setSeedMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchOwnerData = async () => {
      if (!owner) return;

      try {
        setIsLoading(true);

        // Fetch SIMs for this owner
        const simsResponse = await fetch(`/api/v1/sims?pageSize=1000&q=${encodeURIComponent(owner)}`);
        const simsData = await simsResponse.json();

        // Fetch modems for this owner
        const modemsResponse = await fetch(`/api/v1/modems?pageSize=1000&q=${encodeURIComponent(owner)}`);
        const modemsData = await modemsResponse.json();

        setData({
          owner: owner,
          sims: simsData.sims || [],
          modems: modemsData.modems || [],
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch owner data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOwnerData();
  }, [owner]);

  const handleSeed = async () => {
    if (!owner) {
      setSeedMessage('Cannot seed: Owner name not found');
      return;
    }

    if (!window.confirm(`Seed all SIMs and modems for owner "${owner}" to customer servers?`)) {
      return;
    }

    setIsSeeding(true);
    setSeedMessage(null);

    try {
      const response = await fetch(`/api/v1/seed/${encodeURIComponent(owner)}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to seed data');
      }

      const result = await response.json();

      // Format detailed message
      const simInfo = `SIMs: ${result.details?.sims?.sent || 0} sent, ${result.details?.sims?.failed?.length || 0} failed`;
      const modemInfo = `Modems: ${result.details?.modems?.sent || 0} sent, ${result.details?.modems?.failed?.length || 0} failed`;
      setSeedMessage(`${result.message}\n${simInfo}\n${modemInfo}`);
    } catch (err) {
      setSeedMessage(err instanceof Error ? err.message : 'Failed to seed data');
    } finally {
      setIsSeeding(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error}
        onRetry={() => navigate(-1)}
      />
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 dark:text-gray-400 text-lg mb-4">
          No data found for owner: {owner}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="text-blue-600 hover:text-blue-800 underline"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-blue-600 hover:text-blue-800 mb-4 inline-block"
        >
          ← Back
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{data.owner}</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Owner Summary - {data.sims.length} SIMs, {data.modems.length} Modems
            </p>
          </div>
          <button
            onClick={handleSeed}
            disabled={isSeeding || (data.sims.length === 0 && data.modems.length === 0)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSeeding ? (
              <>
                <LoadingSpinner size="small" />
                <span className="ml-2">Seeding...</span>
              </>
            ) : (
              'Seed to Customers'
            )}
          </button>
        </div>
      </div>

      {/* Seed Message */}
      {seedMessage && (
        <div className={`mb-6 p-4 rounded-md ${seedMessage.includes('success') || seedMessage.includes('sent') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          <pre className="text-sm whitespace-pre-wrap">{seedMessage}</pre>
        </div>
      )}

      {/* SIMs Table */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          SIM Cards ({data.sims.length})
        </h2>
        {data.sims.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 text-center text-gray-500 dark:text-gray-400">
            No SIM cards found for this owner
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      ICCID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      IMSI
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Label
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                  {data.sims.map((sim) => (
                    <tr key={sim.iccid} className="hover:bg-gray-50 dark:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {sim.iccid}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {sim.imsi || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {sim.label || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {sim.ip_address || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          sim.status?.connectivity_status?.status === 'online'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800 dark:text-gray-200'
                        }`}>
                          {sim.status?.connectivity_status?.status || 'unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Link
                          to={`/sim/${sim.iccid}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modems Table */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Modems ({data.modems.length})
        </h2>
        {data.modems.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 text-center text-gray-500 dark:text-gray-400">
            No modems found for this owner
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Modem ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Serial Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      IMEI
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Model
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      IP Address
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                  {data.modems.map((modem) => (
                    <tr key={modem.modem_id} className="hover:bg-gray-50 dark:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {modem.modem_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {modem.serial_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {modem.imei || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {modem.model || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          modem.status === 'Active'
                            ? 'bg-green-100 text-green-800'
                            : modem.status === 'Inactive'
                            ? 'bg-gray-100 text-gray-800 dark:text-gray-200'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {modem.status || 'unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {modem.ip_address || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerSummary;
